import { ApolloError, DocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { useGraphql } from 'utils/hooks/useGraphql';
import noop from 'utils/noop';
import { isDefined } from 'utils/tsHelpers';
import { GraphQLTemplateCampaign, TemplateCampaign } from '../common/types';

export type FetchCampaignByIdQueryRequest = {
  campaignId: number;
};

type FetchCampaignByIdQueryResponse = {
  campaign: GraphQLTemplateCampaign;
};

export type UseCampaignByIdResponse = {
  errorMessage?: ApolloError;
  hasError: boolean;
  isLoading: boolean;
  refetch: (variables?: Partial<FetchCampaignByIdQueryRequest>) => void;
  results: TemplateCampaign;
};

export const fetchCampaignByIdQuery = gql`
  query FetchCampaignById($campaignId: Long!) {
    campaign(id: $campaignId) {
      campaignState
      campaignTypeDefinition
      id
      name
      experiments {
        id
        experimentType
        name
      }
      lists {
        id
      }
      mobileAppIds
    }
  }
`;

export function useCampaignById(
  options: Record<string, any>,
  query: DocumentNode = fetchCampaignByIdQuery, // eslint-disable-line
): UseCampaignByIdResponse {
  const queryOptions = useMemo(
    () => ({
      notifyOnNetworkStatusChange: true,
      ...options,
    }),
    [options],
  );
  const {
    data: fetchCampaignByIdQueryData,
    error: hasError = false,
    errorMessage,
    isLoading,
    refetch,
  } = useGraphql(query, queryOptions);

  // protect against the undefined response while the query is running
  if (isDefined<FetchCampaignByIdQueryResponse>(fetchCampaignByIdQueryData)) {
    const results = fetchCampaignByIdQueryData.campaign || {};

    return {
      errorMessage,
      hasError,
      isLoading,
      refetch,
      results,
    };
  }

  return {
    errorMessage,
    hasError,
    isLoading,
    refetch: noop,
    results: {} as TemplateCampaign,
  };
}
